import React, { FC, PropsWithChildren } from 'react';

import { ExternalLeft24, ExternalRight24, IconButton, Stack, Typography, useMobile } from 'Modules/ui3';

import { ControlButtonProps, PageNumberButtonProps, PagingControlsProps } from './types';
import { ControlsContext, useControlsContext } from './utils';


const ControlButton: FC<PropsWithChildren<ControlButtonProps>> = ({
    onClick, selected, children,
}) => {
    const { disabled } = useControlsContext();
    const isMobile = useMobile();
    return (
        <IconButton
            data-testid={selected ? 'selected' : 'base'}
            variant='secondary'
            size={isMobile ? 'extraLarge' : 'extraExtraLarge'}
            isRound
            onClick={onClick}
            disableRipple
            disabled={disabled}
            sx={{
                border: 0,
                borderRadius: isMobile ? 5 : 6,
                bgcolor: selected ? '#DBDBDB' : '#f5f5f5',
                '&>svg': {
                    color: '#242424',
                },
                '&:hover': {
                    bgcolor: selected ? '#DBDBDB' : '#E6E6E6',
                },
                '&:focus': {
                    bgcolor: '#DBDBDB',
                },
                '&:disabled': {
                    color: '#f5f5f5',
                    '>svg': {
                        color: '#D5D4D3',
                    },
                },
            }}
        >
            {children}
        </IconButton>
    );
};

const Dots: FC = () => (
    <Typography fontSize='20px' fontWeight='400' lineHeight='1' px={1} py={3} color='grayscale.shade95'>
        ...
    </Typography>
);

const PageNumberButton: FC<PageNumberButtonProps> = ({ pageNumber }) => {
    const { onPageNumber, currentPage } = useControlsContext();
    return (
        <ControlButton
            onClick={() => onPageNumber(pageNumber)}
            selected={currentPage === pageNumber}
        >
            <Typography minWidth='24px' fontSize='20px' lineHeight='1' fontWeight='400'>
                {pageNumber}
            </Typography>
        </ControlButton>
    );
};

export const PagingControls: FC<PagingControlsProps> = ({
    currentPage, disabled,
    onChange,
    onNext = onChange,
    onPrevious = onChange,
    onPageNumber = onChange,
    pageCount,
    showNumbers,
}) => (
    <Stack direction='row' spacing={2}>
        <ControlsContext.Provider
            value={{
                currentPage,
                disabled: disabled || pageCount < 2,
                onPageNumber,
            }}
        >
            <ControlButton onClick={() =>
                onPrevious(currentPage <= 1 ? pageCount : currentPage - 1)}
            >
                <ExternalLeft24 />
            </ControlButton>
            {showNumbers && (
                <Stack direction='row' spacing={1}>
                    <PageNumberButton pageNumber={1} />
                    {currentPage > 2 && pageCount > 3 && <Dots />}
                    {pageCount > 1 && (
                        <PageNumberButton
                            pageNumber={
                                currentPage > 1 && currentPage < pageCount
                                    ? currentPage
                                    : currentPage === pageCount && pageCount > 3
                                        ? pageCount - 1
                                        : 2
                            }
                        />
                    )}
                    {pageCount > 3 && pageCount - currentPage > 1 && <Dots />}
                    {pageCount > 2 && <PageNumberButton pageNumber={pageCount} />}
                </Stack>
            )}
            <ControlButton onClick={() => onNext(currentPage >= pageCount ? 1 : currentPage + 1)}>
                <ExternalRight24 />
            </ControlButton>
        </ControlsContext.Provider>
    </Stack>
);
